/**
 * OEE Dashboard Constants
 * Color schemes, state configurations, and other constants
 */

import { MachineState, DowntimeReasonType } from './machine-event.model'
import { RateUnit } from './oee-metrics.model'

/**
 * Machine State Colors
 * Standard color coding for machine states across the dashboard
 * Unified color scheme:
 * - Green: Running/production
 * - Red: Unplanned downtime
 * - Blue: Planned downtime
 * - Gray: Idle (synthesized gaps)
 */
export const MACHINE_STATE_COLORS: Record<MachineState, string> = {
    RUNNING: '#10B981',    // Green - ongoing run
    COMPLETED: '#059669',  // Darker green - completed run
    DOWN: '#EF4444',       // Red - unplanned downtime (default)
    PLANNED: '#3B82F6',    // Blue - planned downtime (e.g. scheduled maintenance)
    IDLE: '#9CA3AF',       // Gray - idle / had work recently / none right now
    INACTIVE: '#D1D5DB',   // Lighter gray - no recent events at all
    SCHEDULED: '#F59E0B',  // Orange/amber - batch scheduled, not started yet
};

/**
 * Downtime Reason Type Colors
 * Color coding based on reason type (planned vs unplanned)
 */
export const DOWNTIME_REASON_TYPE_COLORS: Record<DowntimeReasonType, string> = {
    planned: '#3B82F6',    // Blue - planned downtime (maintenance, changeover)
    unplanned: '#EF4444',  // Red - unplanned downtime (breakdown, error)
    idle: '#9CA3AF',       // Gray - idle with no specific reason
};

/**
 * Performance Indicator Colors
 * Color coding based on performance vs target
 */
export const PERFORMANCE_COLORS = {
    GOOD: '#10B981', // >= Target
    WARNING: '#F59E0B', // 80-99% of Target
    POOR: '#EF4444', // < 80% of Target
};


/**
 * Get performance color based on value vs target
 */
export function getPerformanceColor(value: number, target: number): string {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return PERFORMANCE_COLORS.GOOD;
    if (percentage >= 80) return PERFORMANCE_COLORS.WARNING;
    return PERFORMANCE_COLORS.POOR;
}

/**
 * Get performance class name for styling
 */
export function getPerformanceClass(value: number, target: number): string {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return 'performance-good';
    if (percentage >= 80) return 'performance-warning';
    return 'performance-poor';
}

/**
 * Machine state display labels
 */
export const MACHINE_STATE_LABELS: Record<MachineState, string> = {
    RUNNING: 'Active',
    COMPLETED: 'Idle',
    DOWN: 'Down',
    PLANNED: 'Planned',
    IDLE: 'Idle',
    INACTIVE: 'Inactive',
    SCHEDULED: 'Scheduled',
};

/**
 * Event type icons (FontAwesome classes)
 */
export const EVENT_TYPE_ICONS = {
    STATE_CHANGE: 'fa-exchange-alt',
    PRODUCTION_COUNT: 'fa-boxes',
    MAINTENANCE: 'fa-wrench',
    INCIDENT: 'fa-exclamation-circle',
};

/**
 * Production Rate Conversion Utilities
 */

/**
 * Convert rate from PPM (canonical unit) to specified display unit
 */
export function convertFromPPM(ratePPM: number, toUnit: RateUnit): number {
    switch (toUnit) {
        case 'ppm':
            return ratePPM;
        case 'pph':
            return ratePPM * 60; // PPM × 60 = PPH
        case 'ppd':
            return ratePPM * 60 * 24; // PPM × 60 × 24 = PPD
        case 'ppw':
            return ratePPM * 60 * 24 * 7; // PPM × 60 × 24 × 7 = PPW
        case 'ppmo':
            return ratePPM * 60 * 24 * 30; // PPM × 60 × 24 × 30 = PPMO (approx)
        default:
            return ratePPM;
    }
}

/**
 * Convert rate from specified display unit to PPM (canonical unit)
 */
export function convertToPPM(rate: number, fromUnit: RateUnit): number {
    switch (fromUnit) {
        case 'ppm':
            return rate;
        case 'pph':
            return rate / 60; // PPH ÷ 60 = PPM
        case 'ppd':
            return rate / (60 * 24); // PPD ÷ (60 × 24) = PPM
        case 'ppw':
            return rate / (60 * 24 * 7); // PPW ÷ (60 × 24 × 7) = PPM
        case 'ppmo':
            return rate / (60 * 24 * 30); // PPMO ÷ (60 × 24 × 30) = PPM
        default:
            return rate;
    }
}

/**
 * Determine the optimal display unit for a given rate in PPM
 * For slow processes, automatically select a larger unit to avoid tiny decimals
 */
export function getOptimalRateUnit(ratePPM: number): RateUnit {
    // If rate is less than 0.01 ppm (less than 0.6 per hour), use per day
    if (ratePPM < 0.0001) {
        return 'ppmo';
    }
    if (ratePPM < 0.001) {
        return 'ppw';
    }
    if (ratePPM < 0.01) {
        return 'ppd';
    }
    // If rate is less than 1 ppm (less than 60 per hour), use per hour
    if (ratePPM < 1) {
        return 'pph';
    }
    // Otherwise use per minute
    return 'ppm';
}

/**
 * Format a rate value with appropriate precision for the given unit
 * Avoids ugly 9-decimal values while maintaining useful precision
 */
export function formatRateValue(rate: number, unit: RateUnit): string {
    let decimals = 1; // Default precision

    // Adjust precision based on unit and magnitude
    if (unit === 'ppm') {
        // PPM: show 4 decimals for small values (< 10), 2 otherwise
        decimals = rate < 10 ? 4 : 2;
    } else if (unit === 'pph') {
        // PPH: show 2 decimals
        decimals = 2;
    } else if (unit === 'ppd') {
        // PPD: show 1 decimal
        decimals = 1;
    } else if (unit === 'ppw' || unit === 'ppmo') {
        // PPW/PPMO: usually large numbers, no decimals needed
        decimals = 0;
    }

    return rate.toFixed(decimals);
}

/**
 * Format a rate with unit label
 */
export function formatRateWithUnit(ratePPM: number, displayUnit: RateUnit): string {
    const displayRate = convertFromPPM(ratePPM, displayUnit);
    const formattedValue = formatRateValue(displayRate, displayUnit);
    return `${formattedValue} ${displayUnit}`;
}
